import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from './contexts/AuthContext';

// Analytics & SEO
import GoogleAnalytics from './components/GoogleAnalytics';
import SEO from './components/SEO';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ChildProgress from './pages/ChildProgress';
import GamePlayer from './pages/GamePlayer';
import Pricing from './pages/Pricing';
import GamesLibrary from './pages/GamesLibrary';
import ForTherapists from './pages/ForTherapists';
import ForSchools from './pages/ForSchools';
import ForParents from './pages/ForParents';
import ForCreatives from './pages/ForCreatives';
import OurApproach from './pages/OurApproach';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import AdminDashboard from './pages/admin/AdminDashboard';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Protected Route wrapper
function ProtectedRoute({ children, adminOnly = false }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Route (redirect if logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default function App() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Pages where footer should NOT appear
  const noFooterPages = ['/play', '/admin'];
  const showFooter = !noFooterPages.some(page => location.pathname.startsWith(page));

  return (
    <HelmetProvider>
      <div className="min-h-screen flex flex-col">
        {/* Google Analytics - tracks all page views automatically */}
        <GoogleAnalytics />
        
        {/* Default SEO - can be overridden by individual pages */}
        <SEO />
        
        {/* Scroll to top on route change */}
        <ScrollToTop />
        
        {/* Show navbar on all pages except game player */}
        <Routes>
          <Route path="/play/:gameId" element={null} />
          <Route path="*" element={<Navbar />} />
        </Routes>

        {/* Main content */}
        <main className="flex-grow">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/games" element={<GamesLibrary />} />
            <Route path="/for-therapists" element={<ForTherapists />} />
            <Route path="/for-schools" element={<ForSchools />} />
            <Route path="/for-parents" element={<ForParents />} />
            <Route path="/for-creatives" element={<ForCreatives />} />
            <Route path="/our-approach" element={<OurApproach />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />

            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/play/:gameId" 
              element={
                <ProtectedRoute>
                  <GamePlayer />
                </ProtectedRoute>
              } 
            />

            {/* One child's progress chart. Per child, because the chart is a
                property of the child and not of the account. */}
            <Route
              path="/progress/:childId"
              element={
                <ProtectedRoute>
                  <ChildProgress />
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route 
              path="/admin/*" 
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* Global Footer - show on all pages except game player and admin */}
        {showFooter && <Footer />}
      </div>
    </HelmetProvider>
  );
}
